import React, { useEffect, useState } from 'react';
import { Box, styled } from '@mui/system';

import { Typography } from '@mui/material';

import BalanceIcon from '@mui/icons-material/PaymentsOutlined';
import CreditIcon from '@mui/icons-material/CreditScoreOutlined';
import AvailableLimitIcon from '@mui/icons-material/MonetizationOnOutlined';
import SbButton from '../../components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '../../store';
import { getOutstandingBalanceForPrepaid, getWalletBalance } from '@Reducers/Wallet';
import AddMoneyModal from '../Transactions/Wallet/AddMoneyModal';
import { getCreditLimit, getOutstandingBalanceForPostpaid } from '@Reducers/Credit';
import { formatNumber, useLocale } from '../../utils/formatNumber';
import { getOutstandingBalanceInvoiceForPostpaid } from '@Reducers/Credit/actions';
import { getOutstandingBalanceInvoiceForPrepaid } from '@Reducers/Wallet/actions';
import { userLogin } from '@Services/hasAdminAccess';
import WarningIcon from '@mui/icons-material/Warning';

const NavigationBtnContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gridGap: '24px',
  alignItems: 'stretch',
  [theme.breakpoints.down('xl')]: {
    display: 'flex',
    flexWrap: 'wrap',
  },
}));

const NavigationBtn = styled('div')<{
  isOverLimit?: boolean;
}>(({ theme, isOverLimit }) => ({
  padding: '20px',
  display: 'grid',
  gridTemplateColumns: '32px 1fr',
  alignItems: 'center',
  border: '1px solid',
  borderRadius: '8px',
  borderColor: isOverLimit ? theme.palette.error.main : 'var(--mui-palette-border-light)',
  backgroundColor: isOverLimit ? '#fff5f5' : 'transparent', // light red background if over limit
  color: isOverLimit ? theme.palette.error.main : 'inherit',
  height: '100%',
  [theme.breakpoints.down('xl')]: {
    width: 340,
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const IconStyle = { width: '33px', height: '33px' };

function Navigation() {
  const dispatch = useDispatch<AppDispatch>();
  const loginUser = useSelector((state: RootStateType) => state.loginUser);
  const [addMoneyModal, setAddMoneyModal] = useState<boolean>(false);

  const { currentBalance, totalInvoiceAmtPaid, totalShipmentAmt, totalShipmentAmtPaid } = useSelector(
    (state: RootStateType) => state.Wallet,
  );
  const {
    creditLimit,
    totalInvoiceAmtPaid: totalInvoiceAmtPaidCredit,
    totalShipmentAmt: totalShipmentAmtCredit,
    totalShipmentAmtPaid: totalShipmentAmtPaidCredit,
  } = useSelector((state: RootStateType) => state.Credit);
  const orgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.symbol || '',
  );
  const isAccountHolder = loginUser?.profile?._accountId?.isAccountHolder;
  const isPrepaid = loginUser?.profile?._accountId?.isPrepaid;
  const locale = useLocale();
  const outStandingBalanceResult = totalShipmentAmt - totalShipmentAmtPaid - totalInvoiceAmtPaid;
  const outStandingBalanceResultCredit =
    totalShipmentAmtCredit - totalShipmentAmtPaidCredit - totalInvoiceAmtPaidCredit;

  useEffect(() => {
    if (typeof isAccountHolder !== 'boolean') return;

    if (isAccountHolder) {
      dispatch(getWalletBalance());
      dispatch(getOutstandingBalanceForPostpaid());
      dispatch(getOutstandingBalanceInvoiceForPostpaid());
    } else {
      dispatch(getCreditLimit());
      dispatch(getOutstandingBalanceForPrepaid());
      dispatch(getOutstandingBalanceInvoiceForPrepaid());
    }
  }, [isAccountHolder]);

  const availableLimit = creditLimit - outStandingBalanceResultCredit;
  const isUserLogin = userLogin();

  const outstandingBalance = isAccountHolder ? outStandingBalanceResultCredit : outStandingBalanceResult;

  return (
    <NavigationBtnContainer>
      {addMoneyModal && <AddMoneyModal onClose={() => setAddMoneyModal(false)} />}
      <NavigationBtn>
        <BalanceIcon style={IconStyle} />

        <div className="ml-4">
          <Typography className="md-text">Outstanding balance</Typography>
          <Typography className="l-text">
            {orgCountryCurrency} {orgCountryCurrency} {formatNumber(outstandingBalance, locale)}
          </Typography>
        </div>
      </NavigationBtn>
      {isAccountHolder && !isPrepaid && (
        <>
          <NavigationBtn>
            <CreditIcon style={IconStyle} />

            <div className="ml-4">
              <Typography className="md-text">Credit Limit</Typography>
              <Typography className="l-text">
                {orgCountryCurrency} {formatNumber(creditLimit, locale)}
              </Typography>
            </div>
          </NavigationBtn>
          <Box sx={{ height: '100%' }}>
            <NavigationBtn isOverLimit={availableLimit < 0}>
              <AvailableLimitIcon style={IconStyle} />
              <div className="ml-4">
                <Typography color={availableLimit < 0 ? 'error' : 'inherit'} className="md-text">
                  Available Limit
                </Typography>
                <Typography color={availableLimit < 0 ? 'error' : 'inherit'} className="l-text">
                  {/* {orgCountryCurrency} {availableLimit} */}
                  {orgCountryCurrency} {availableLimit?.toFixed(2)}
                </Typography>
              </div>
            </NavigationBtn>
            {availableLimit < 0 && (
              <Typography
                color="error"
                variant="body2"
                sx={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}
              >
                <WarningIcon />{' '}
                {isUserLogin
                  ? 'Your account has exceeded its credit limit.'
                  : 'This account has exceeded its credit limit.'}
              </Typography>
            )}
          </Box>
        </>
      )}
      {isPrepaid && (
        <NavigationBtn style={{ gridTemplateColumns: '32px 1fr 1fr', gap: '10px' }}>
          <CreditIcon style={IconStyle} />
          <div className="ml-4">
            <Typography className="md-text">Wallet Balance</Typography>
            <Typography className="l-text">
              {orgCountryCurrency} {formatNumber(currentBalance, locale) || 0}
            </Typography>
          </div>
          <SbButton
            className="md-text"
            variant="outlined"
            sx={{ borderRadius: '8px !important', gridColumn: { xs: '1 / 3', md: 'unset' } }}
            onClick={() => setAddMoneyModal(true)}
          >
            Add Money
          </SbButton>
        </NavigationBtn>
      )}
    </NavigationBtnContainer>
  );
}

export default Navigation;
